import { useState } from 'react'
import {
  DueDiligenceStatuses,
  type SupplyChainNode,
  type SupplyChainData,
  type DueDiligenceStatus,
} from '@esf/shared'

interface SupplyChainFormProps {
  supplyChain: SupplyChainData | null
  onChange: (supplyChain: SupplyChainData) => void
  disabled?: boolean
}

const DueDiligenceStatusLabels: Record<DueDiligenceStatus, string> = {
  VERIFIED: 'Verified',
  PENDING_VERIFICATION: 'Pending Verification',
  NOT_VERIFIED: 'Not Verified',
  AUDIT_SCHEDULED: 'Audit Scheduled',
  FAILED_AUDIT: 'Failed Audit',
}

const emptyNode: SupplyChainNode = {
  tier: 1,
  operatorName: '',
  operatorCountry: '',
  componentSupplied: '',
  certifications: [],
  dueDiligenceStatus: 'PENDING_VERIFICATION',
}

const defaultSupplyChain: SupplyChainData = {
  nodes: [],
  dueDiligencePolicyUrl: '',
  conflictMineralsFree: undefined,
  lastAuditDate: '',
}

export default function SupplyChainForm({
  supplyChain,
  onChange,
  disabled,
}: SupplyChainFormProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [certInput, setCertInput] = useState<Record<number, string>>({})

  const data = supplyChain || defaultSupplyChain

  const updateField = <K extends keyof SupplyChainData>(
    field: K,
    value: SupplyChainData[K]
  ) => {
    onChange({ ...data, [field]: value })
  }

  const addNode = () => {
    const maxTier = data.nodes.reduce((max, n) => Math.max(max, n.tier), 0)
    const newNodes = [...data.nodes, { ...emptyNode, tier: maxTier + 1 }]
    onChange({ ...data, nodes: newNodes })
    setExpandedIndex(newNodes.length - 1)
  }

  const removeNode = (index: number) => {
    const newNodes = data.nodes.filter((_, i) => i !== index)
    onChange({ ...data, nodes: newNodes })
    if (expandedIndex === index) {
      setExpandedIndex(null)
    }
  }

  const updateNode = (index: number, field: keyof SupplyChainNode, value: string | number | string[]) => {
    const newNodes = data.nodes.map((n, i) =>
      i === index ? { ...n, [field]: value } : n
    )
    onChange({ ...data, nodes: newNodes })
  }

  const handleCertChange = (index: number, value: string) => {
    setCertInput(prev => ({ ...prev, [index]: value }))
    const certs = value.split(',').map(s => s.trim()).filter(Boolean)
    updateNode(index, 'certifications', certs)
  }

  // Group nodes by tier
  const nodesByTier = data.nodes.reduce((acc, node, index) => {
    const tier = node.tier || 1
    if (!acc[tier]) acc[tier] = []
    acc[tier].push({ node, index })
    return acc
  }, {} as Record<number, { node: SupplyChainNode; index: number }[]>)

  const tiers = Object.keys(nodesByTier).map(Number).sort()

  return (
    <div className="space-y-6">
      {/* Policy Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-3">Due Diligence Policy</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-blue-700 mb-1">Policy Document URL</label>
            <input
              type="url"
              value={data.dueDiligencePolicyUrl || ''}
              onChange={e => updateField('dueDiligencePolicyUrl', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="https://example.com/due-diligence-policy.pdf"
            />
            <p className="text-xs text-blue-600 mt-1">Link to OECD-aligned due diligence policy</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Conflict Minerals Status</label>
              <select
                value={data.conflictMineralsFree === undefined ? '' : data.conflictMineralsFree.toString()}
                onChange={e => updateField('conflictMineralsFree', e.target.value === '' ? undefined : e.target.value === 'true')}
                disabled={disabled}
                className="filter-select w-full"
              >
                <option value="">Not assessed</option>
                <option value="true">Conflict-free certified</option>
                <option value="false">Contains conflict minerals</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-1">Last Audit Date</label>
              <input
                type="date"
                value={data.lastAuditDate?.split('T')[0] || ''}
                onChange={e => updateField('lastAuditDate', e.target.value)}
                disabled={disabled}
                className="filter-input w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Supply Chain Nodes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-medium text-gray-700">Supply Chain Traceability</h4>
            <p className="text-xs text-gray-500">Multi-tier supplier transparency per ESPR requirements</p>
          </div>
          <button
            type="button"
            onClick={addNode}
            disabled={disabled}
            className="btn btn-secondary text-sm py-1 px-3"
          >
            + Add Supplier
          </button>
        </div>

        {data.nodes.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No supply chain data</p>
            <p className="text-xs text-gray-400">Add suppliers to track traceability</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tiers.map(tier => (
              <div key={tier} className="relative">
                {/* Tier Header */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-esf-navy text-white text-xs font-bold">
                    {tier}
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    Tier {tier} {tier === 1 ? '(Direct Suppliers)' : tier === 2 ? '(Sub-Suppliers)' : '(Raw Materials)'}
                  </span>
                </div>

                {/* Tier Nodes */}
                <div className="ml-8 space-y-2">
                  {nodesByTier[tier].map(({ node, index }) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Node Header */}
                      <div
                        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            node.dueDiligenceStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                            node.dueDiligenceStatus === 'FAILED_AUDIT' ? 'bg-red-100 text-red-800' :
                            node.dueDiligenceStatus === 'PENDING_VERIFICATION' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {DueDiligenceStatusLabels[node.dueDiligenceStatus]}
                          </span>
                          <span className="font-medium text-sm">
                            {node.operatorName || 'Unnamed supplier'}
                          </span>
                          {node.operatorCountry && (
                            <span className="text-xs text-gray-500">{node.operatorCountry}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {node.componentSupplied && (
                            <span className="text-xs text-gray-500">{node.componentSupplied}</span>
                          )}
                          <svg
                            className={`h-5 w-5 text-gray-400 transition-transform ${
                              expandedIndex === index ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {expandedIndex === index && (
                        <div className="px-4 py-4 space-y-4 border-t border-gray-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Supplier Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={node.operatorName}
                                onChange={e => updateNode(index, 'operatorName', e.target.value)}
                                disabled={disabled}
                                className="filter-input w-full"
                                placeholder="Company name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                              <input
                                type="text"
                                value={node.operatorCountry || ''}
                                onChange={e => updateNode(index, 'operatorCountry', e.target.value)}
                                disabled={disabled}
                                className="filter-input w-full"
                                placeholder="e.g., Germany"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Tier Level</label>
                              <select
                                value={node.tier}
                                onChange={e => updateNode(index, 'tier', parseInt(e.target.value))}
                                disabled={disabled}
                                className="filter-select w-full"
                              >
                                <option value={1}>Tier 1 - Direct Supplier</option>
                                <option value={2}>Tier 2 - Sub-Supplier</option>
                                <option value={3}>Tier 3 - Raw Materials</option>
                                <option value={4}>Tier 4 - Origin</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Due Diligence Status</label>
                              <select
                                value={node.dueDiligenceStatus}
                                onChange={e => updateNode(index, 'dueDiligenceStatus', e.target.value)}
                                disabled={disabled}
                                className="filter-select w-full"
                              >
                                {DueDiligenceStatuses.map(status => (
                                  <option key={status} value={status}>{DueDiligenceStatusLabels[status]}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Component Supplied</label>
                            <input
                              type="text"
                              value={node.componentSupplied}
                              onChange={e => updateNode(index, 'componentSupplied', e.target.value)}
                              disabled={disabled}
                              className="filter-input w-full"
                              placeholder="e.g., Steel frame components"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Certifications</label>
                            <input
                              type="text"
                              value={certInput[index] ?? (node.certifications || []).join(', ')}
                              onChange={e => handleCertChange(index, e.target.value)}
                              disabled={disabled}
                              className="filter-input w-full"
                              placeholder="ISO 14001, ISO 9001 (comma-separated)"
                            />
                            {(node.certifications?.length || 0) > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {node.certifications?.map((cert, i) => (
                                  <span key={i} className="badge bg-blue-100 text-blue-800">{cert}</span>
                                ))}
                              </div>
                            )}
                          </div>

                          {node.operatorGln && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">GLN</label>
                              <input
                                type="text"
                                value={node.operatorGln || ''}
                                onChange={e => updateNode(index, 'operatorGln', e.target.value)}
                                disabled={disabled}
                                className="filter-input w-full"
                                placeholder="Global Location Number"
                              />
                            </div>
                          )}

                          <div className="flex justify-end pt-2">
                            <button
                              type="button"
                              onClick={() => removeNode(index)}
                              disabled={disabled}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Remove Supplier
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
